/**
 * DistrictSearch — autocomplete for finding a district / neighborhood by name.
 *
 * Debounced lookups against `/locations/areas/search` (EN + AR), optional city
 * scope. On select it hands the chosen area to the caller (the search page sets
 * the `areaId` filter; the home search navigates to `/search?areaId=`).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { searchAreas, type AreaSuggestion } from '../../services/neighborhoodMapService';

interface DistrictSearchProps {
  onSelect: (area: AreaSuggestion) => void;
  /** Scope suggestions to a city (optional). */
  cityId?: string;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

export default function DistrictSearch({
  onSelect,
  cityId,
  placeholder,
  className = '',
  autoFocus = false,
}: DistrictSearchProps) {
  const { t, i18n } = useTranslation('search');
  const isRTL = i18n.language === 'ar';

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<AreaSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);

  const localizedName = useCallback(
    (a: AreaSuggestion) => (isRTL ? a.nameAr || a.nameEn : a.nameEn),
    [isRTL],
  );

  // Debounced lookup. A request id guards against out-of-order responses.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 1) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const data = await searchAreas(term, cityId);
        if (!cancelled) {
          setResults(data);
          setOpen(true);
          setActiveIndex(-1);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, cityId]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const choose = (area: AreaSuggestion) => {
    onSelect(area);
    setQuery('');
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      choose(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <i className={`ri-map-pin-2-line absolute top-1/2 -translate-y-1/2 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`}></i>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          placeholder={placeholder ?? t('search.district.placeholder', { defaultValue: isRTL ? 'ابحث عن حي…' : 'Search a district…' })}
          className={`w-full py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#004D40] ${isRTL ? 'pr-9 pl-3' : 'pl-9 pr-3'}`}
          aria-label={t('search.district.label', { defaultValue: isRTL ? 'البحث عن حي' : 'Search district' })}
        />
        {loading && (
          <i className={`ri-loader-4-line animate-spin absolute top-1/2 -translate-y-1/2 text-[#004D40] ${isRTL ? 'left-3' : 'right-3'}`}></i>
        )}
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden max-h-72 overflow-y-auto">
          {results.length === 0 ? (
            !loading && (
              <div className="px-4 py-3 text-sm text-gray-500">
                {t('search.district.noResults', { defaultValue: isRTL ? 'لا توجد أحياء مطابقة' : 'No matching districts' })}
              </div>
            )
          ) : (
            results.map((area, idx) => (
              <button
                key={area.id}
                type="button"
                onMouseEnter={() => setActiveIndex(idx)}
                onClick={() => choose(area)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-start ${
                  idx === activeIndex ? 'bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <i className="ri-map-pin-2-line text-[#004D40] flex-shrink-0"></i>
                  <span className="truncate text-sm text-gray-900 dark:text-white">{localizedName(area)}</span>
                </span>
                <span className="text-xs text-gray-400 whitespace-nowrap">
                  {t('search.district.listings', { count: area.activeListingCount, defaultValue: `${area.activeListingCount} listings` })}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
